import { Suspense, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { faChevronDown, faFileExport, faGavel, faQrcode } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchSponsors } from 'api/campaigns';
import * as ExportDialog from 'assets/js/components/ExportDialog';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Import from 'components/imports/Import';
import * as Input from 'components/input';
import { CreateItemDropdown } from 'components/pages/campaign-settings/campaign-auction/items-index/CreateItemDropdown';
import { useItemsIndex } from 'components/pages/campaign-settings/campaign-auction/items-index/useItemsIndex';
import * as AuctionPartnersDialog from 'components/pages/campaign-settings/campaign-auction/sponsors/AuctionPartnersDialog';
import { useSponsorsStore } from 'components/pages/campaign-settings/campaign-auction/sponsors/useSponsorsStore';
import { useCampaignItemRoutes } from 'components/pages/campaign-settings/campaign-auction/useCampaignItemRoutes';
import * as Table from 'components/table';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Sponsor } from 'types/domain';

const ItemsIndex = () => {
    const { table, campaignId, search, setSearch, isPending, searchTerm } = useItemsIndex();
    const itemRoutes = useCampaignItemRoutes();
    const [show, setShow] = useState(false);
    const { accountId } = useCurrentAccount();
    const { setActiveSponsor, setShowAcceptTerms, setShowSponsorsItemsModal } = useSponsorsStore();
    const history = useHistory();

    /**
     * For now, we only have Inspirato as a sponsor. When we add more sponsors, we will need to update this logic.
     * I believe in the future, the terms will only be shown when the user select a sponsor, making this logic obsolete.
     */
    const { isSuccess, data: sponsors } = useSuspenseQuery({
        queryKey: campaignQueryKeys.sponsorTerms(campaignId),
        queryFn: () => fetchSponsors(accountId, { page: 1 }),
        select: data => data?.data ?? [],
    });

    useEffect(() => {
        if (isSuccess) {
            const sponsor = sponsors.find((sponsor: Sponsor) => sponsor.service === 'inspirato');
            const enableModal =
                sponsor?.sponsor_account_action_required === 'none' ||
                sponsor?.sponsor_account_action_required === 'accept_terms';

            if (enableModal) {
                setShowSponsorsItemsModal(true);
                setActiveSponsor(sponsor);
                setShowAcceptTerms(sponsor?.sponsor_account_action_required === 'accept_terms');
            }
        }
    }, [sponsors, isSuccess, setActiveSponsor, setShowAcceptTerms, setShowSponsorsItemsModal]);

    return (
        <>
            <AlertDialog.Root open={show} onOpenChange={() => setShow(!show)}>
                <AlertDialog.Portal>
                    <AlertDialog.Overlay />
                    <AlertDialog.Content
                        onEscapeKeyDown={e => e.preventDefault()}
                        className={'w-96'}
                    >
                        <AlertDialog.Header>
                            <div
                                className={
                                    'flex h-12 w-12 flex-col items-center justify-center rounded-full bg-gray-100 text-center align-middle text-gray-500'
                                }
                            >
                                <FontAwesomeIcon className={'flex h-5 w-5'} icon={faQrcode} />
                            </div>
                            <AlertDialog.Close onClick={() => setShow(!show)} />
                        </AlertDialog.Header>
                        <AlertDialog.Body>
                            <AlertDialog.Title className={'text-xl'}>
                                Export QR codes
                            </AlertDialog.Title>
                            <AlertDialog.Description>
                                Choose to include item details alongside QR codes or download QR
                                codes only when exporting as a printable PDF.
                            </AlertDialog.Description>
                        </AlertDialog.Body>
                        <AlertDialog.Footer className={'justify-between gap-4'}>
                            <Button
                                variant="outline"
                                color="neutral"
                                size="sm"
                                className={'h-10 w-full'}
                                onClick={() => window.open(itemRoutes.exportQRCode(), '_blank')}
                            >
                                QR codes only
                            </Button>
                            <Button
                                variant="solid"
                                size="sm"
                                onClick={() =>
                                    window.open(itemRoutes.exportQRCodeDetailed(), '_blank')
                                }
                                className={'h-10 w-full'}
                            >
                                QR codes & details
                            </Button>
                        </AlertDialog.Footer>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
            <div className={'flex flex-col gap-y-4'}>
                <Table.Root
                    isLoading={isPending}
                    table={table}
                    searchTerm={searchTerm}
                    variant="bordered"
                >
                    <p
                        data-intercom-target={IntercomProductTourTargets.AuctionItemsTitle}
                        className={'text-2xl font-medium'}
                    >
                        Items
                    </p>

                    <div className={'flex items-center justify-between'}>
                        <Input.Root>
                            <Input.SearchIcon />
                            <Input.Form
                                placeholder={'Search'}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </Input.Root>
                        <div className={'flex flex-row gap-x-2'}>
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger asChild>
                                    <Button variant="outline" color="neutral" className="group">
                                        Actions
                                        <FontAwesomeIcon
                                            icon={faChevronDown}
                                            className="group-radix-state-open:rotate-180"
                                        />
                                    </Button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content>
                                    <DropdownMenu.Item
                                        disabled={table.getRowModel().rows.length === 0}
                                        onClick={() => setShow(!show)}
                                    >
                                        Export QR codes
                                    </DropdownMenu.Item>

                                    <DropdownMenu.LinkItem
                                        to={`/accounts/${accountId}/campaigns/${campaignId}/auction/items/categories`}
                                    >
                                        Manage Categories
                                    </DropdownMenu.LinkItem>
                                    <Suspense
                                        fallback={
                                            <DropdownMenu.Item disabled>
                                                Import Items
                                            </DropdownMenu.Item>
                                        }
                                    >
                                        <Import.Root strategy="auction_items">
                                            <Import.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onSelect={e => e.preventDefault()}
                                                >
                                                    Import Items
                                                </DropdownMenu.Item>
                                            </Import.Trigger>
                                            <Import.Space />
                                        </Import.Root>
                                    </Suspense>
                                    <Suspense
                                        fallback={
                                            <DropdownMenu.Item className="flex gap-2" disabled>
                                                <FontAwesomeIcon icon={faFileExport} />
                                                Export to CSV
                                            </DropdownMenu.Item>
                                        }
                                    >
                                        <ExportDialog.Root
                                            reportType="auction_items"
                                            params={{
                                                object: 'campaign',
                                                object_id: campaignId,
                                            }}
                                        >
                                            <ExportDialog.Trigger asChild={true}>
                                                <DropdownMenu.Item
                                                    onSelect={e => e.preventDefault()}
                                                    className="flex gap-2"
                                                >
                                                    <FontAwesomeIcon icon={faFileExport} />
                                                    Export to CSV
                                                </DropdownMenu.Item>
                                            </ExportDialog.Trigger>
                                        </ExportDialog.Root>
                                    </Suspense>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>

                            <CreateItemDropdown />
                        </div>
                    </div>

                    <Box className="overflow-x-auto">
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'primary'}>
                            <Table.EmptyIcon>
                                <FontAwesomeIcon
                                    className="text-xl text-purple-600"
                                    icon={faGavel}
                                />
                            </Table.EmptyIcon>
                            <Table.EmptyTitle>No items found</Table.EmptyTitle>
                            <div className="flex flex-col items-center gap-6 md:min-w-96">
                                <Table.EmptyDescription>
                                    <p>
                                        Add your first auction item or browse packages from our
                                        partners.
                                    </p>
                                </Table.EmptyDescription>
                                <div className="flex gap-3">
                                    <AuctionPartnersDialog.Root>
                                        <AuctionPartnersDialog.Trigger asChild>
                                            <Button variant="outline" color="neutral">
                                                Browse packages
                                            </Button>
                                        </AuctionPartnersDialog.Trigger>
                                    </AuctionPartnersDialog.Root>
                                    <Button
                                        variant="solid"
                                        color="primary"
                                        onClick={() => history.push(itemRoutes.create())}
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                        Create new item
                                    </Button>
                                </div>
                            </div>
                        </Table.Empty>
                    </Box>
                    <Table.Pagination>
                        <Table.PaginationPageNavigation />
                    </Table.Pagination>
                </Table.Root>
            </div>
        </>
    );
};

export { ItemsIndex };
